import { useCallback, useMemo } from "react";

import useLatestValue from "app/custom-hooks/useLatestValue";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { createFiltersManager } from "../services/filters-manager/factory";
import type { IFilterManagerModel } from "../services/filters-manager/types";
import type { IFilterInstance } from "../types/filterInstance";

type FiltersManagerProps<T extends IPlainObject> = {
    filtersValue: Partial<T>;
    filtersConfig: readonly IFilterInstance<T>[];
    onFiltersUpdate: (filters: Partial<T>) => void;
};

// Thin React wrapper: memoizes the manager. The flat↔grouped fold for composite filters lives inside
// `createFiltersManager` (the manager owns grouped/instance-key space), not here.
export const useFiltersManager = <T extends IPlainObject = IPlainObject>({
    filtersValue,
    filtersConfig,
    onFiltersUpdate,
}: FiltersManagerProps<T>): IFilterManagerModel<T> => {
    const onFiltersUpdateRef = useLatestValue(onFiltersUpdate);

    const updateFilters = useCallback((newFilterValue: Partial<T>): void => {
        onFiltersUpdateRef.current(newFilterValue);
    }, []);

    return useMemo(
        () =>
            createFiltersManager<T>({
                filtersValue,
                filtersConfig,
                updateFilters,
            }),
        [filtersValue, filtersConfig, updateFilters],
    );
};
